import React from "react";
import { Icon } from "../Icon/Icon";
import { NumberInput } from "../Inputs/NumberInput/NumberInput";
import * as treeUtils from "utils/treeUtils";
import type { AnyProduct, ProductAccessory } from "interfaces/schemas";
import { useTranslate } from "hooks";
import { ProductImage } from "components/ProductImage";
import { useCopiedItem, useSetQuantity } from "stores/project";

export const ProductItem = () => {
  const copiedItem = useCopiedItem();
  const setQuantity = useSetQuantity();

  //Todo: implement translations
  if (!copiedItem) {
    return <>No product selected</>;
  }
  if (!treeUtils.isAnyProduct(copiedItem.data)) {
    return <>Invalid product</>;
  }
  return (
    <div>
      <ProductItemComponent
        node={copiedItem.data}
        isEditMode={true}
        setQuantity={setQuantity}
      />
      {copiedItem.data.nodes && copiedItem.data.nodes?.length > 0 && (
        <div
          className="border-start border-gray-400"
          style={{
            height: "16px",
            width: "1px",
            marginLeft: "16px",
          }}
        />
      )}
      {copiedItem.data.nodes?.map((accessory) => {
        const item = accessory as ProductAccessory;
        const isLastItem =
          accessory ===
          copiedItem.data.nodes?.[copiedItem.data.nodes.length - 1];
        return (
          <AccessoryLayout key={item.id} isLastItem={isLastItem}>
            <ProductItemComponent
              key={item.id}
              node={item}
              isEditMode={false}
              setQuantity={setQuantity}
            />
          </AccessoryLayout>
        );
      })}
    </div>
  );
};

type ProductItemComponentProps = {
  node: AnyProduct;
  isEditMode?: boolean;
  setQuantity: (quantity: number) => void;
};

const ProductItemComponent = ({
  node,
  isEditMode = false,
  setQuantity,
}: ProductItemComponentProps) => {
  const translate = useTranslate();

  const productName = node.productName || "";
  const pimId = node.pimId;
  const presentation = node.presentation || "";
  return (
    <div
      className={
        "border d-flex border-box align-items-center justify-content-between text-gray-700"
      }
    >
      <div className="d-flex align-items-center">
        {pimId ? (
          <div className="icon-64 m-2 d-flex align-items-center justify-content-center">
            <ProductImage
              width={64}
              height={64}
              pimId={pimId}
              productName={productName}
            />
          </div>
        ) : (
          <Icon className="icon-64 m-2" iconName="squares-light" />
        )}
        <div className="d-flex flex-column">
          <div className="d-flex">
            <span className="fw-bolder fs-md text-break">{productName}</span>
          </div>
          <span className="text-gray-400 text-break">
            {translate("PRESENTATION_ORDER_CODE")}: {presentation}
          </span>
        </div>
      </div>
      <div className="me-4 pe-2 d-flex flex-nowrap">
        <div className="d-flex align-items-center pe-5">
          <span className="me-2">{translate("QUANTITY")}:</span>
          <div style={{ width: "82px" }}>
            {isEditMode ? (
              <NumberInput
                initialValue={node.quantity}
                onSave={setQuantity}
                updateOnChange={true}
                min={1}
                max={9999}
                disabled={false}
                classNames={undefined}
              />
            ) : (
              node.quantity
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const AccessoryLayout = ({
  children,
  isLastItem,
}: {
  children: React.ReactNode;
  isLastItem: boolean;
}) => {
  return (
    <div className="position-relative pb-2" style={{ paddingLeft: "32px" }}>
      <div className="position-absolute h-100" style={{ left: 0, top: 0 }}>
        <div
          className="border-start border-gray-400"
          style={{
            position: "absolute",
            left: "16px",
            top: 0,
            height: isLastItem ? "50%" : "100%",
            width: "1px",
          }}
        />
        <div
          className="border-bottom border-gray-400"
          style={{
            position: "absolute",
            left: "16px",
            top: "50%",
            width: "16px",
            height: "1px",
          }}
        />
      </div>

      {children}
    </div>
  );
};
